import { airflow } from '@aviata/platform-client';
import type { SearchParams } from '@aviata/platform-client';

type ExtendedSearchParams = SearchParams & {
  dep: string;
  arr: string;
  dates: string[];
  isYouth: boolean;
};

// TODO: add sentry logging

const parseHashCode = (hashCode: string) => {
  const params = airflow.utils.parseQuery(hashCode);

  if (params) {
    return params;
  }

  throw new Error('ERR_NO_SEARCH_PARAMS');
};

export const getSearchParams = (hashCode: string): ExtendedSearchParams => {
  const { passengers, itineraries, cabinClass, ...types } =
    parseHashCode(hashCode);
  const [firstRoute] = itineraries;

  return {
    dep: firstRoute.dep,
    arr: firstRoute.arr,
    dates: itineraries.map(({ date }) => date),
    isYouth: passengers.yth > 0,
    passengers,
    cabinClass,
    itineraries,
    ...types,
  };
};

const cityCode = (iata: string) => iata.slice(0, 3);

/**
 * Two one-way legs that form a classic round-trip (A→B then B→A).
 * Two legs with A→B and B→C (multi-city) are not a round-trip pair.
 */
const isRoundTripItineraryPair = (
  first: { dep: string; arr: string },
  second: { dep: string; arr: string },
) =>
  cityCode(first.dep) === cityCode(second.arr) &&
  cityCode(first.arr) === cityCode(second.dep);

/**
 * Detect a multi-city (multi-trip) search from the hash.
 * - 1 itinerary → false (one-way)
 * - 2 itineraries, mirrored A↔B → false (round-trip)
 * - 2 itineraries, not mirrored (e.g. ALA→NQZ, NQZ→CIT) → true
 * - 3+ itineraries → true
 */
export const isMultiTripSearch = (hashCode: string): boolean => {
  try {
    const { itineraries } = parseHashCode(hashCode);
    if (itineraries.length < 2) {
      return false;
    }
    if (itineraries.length > 2) {
      return true;
    }
    return !isRoundTripItineraryPair(itineraries[0], itineraries[1]);
  } catch {
    return false;
  }
};

const buildItinerary = (dep: string, arr: string, date: string) => ({
  dep,
  arr,
  date,
});

const buildItineraries = (dep: string, arr: string, dates: string[]) => {
  const OW = buildItinerary(dep, arr, dates[0]);
  if (dates.length === 1) {
    return [OW];
  }

  const RT = buildItinerary(arr, dep, dates[1]);
  return [OW, RT];
};

export const buildNewHashCode = (oldHashCode: string, newDates: string[]) => {
  const { passengers, itineraries, cabinClass } = parseHashCode(oldHashCode);
  const [firstItinerary] = itineraries;

  return airflow.utils.buildQuery({
    passengers,
    cabinClass,
    itineraries: buildItineraries(
      firstItinerary.dep,
      firstItinerary.arr,
      newDates,
    ),
  });
};
